var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../../UI/Button';
import { Badge } from '../../UI/Badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
// Tab state persistence helper
var getStoredActiveTab = function (sessionId, defaultTab) {
    try {
        var stored = localStorage.getItem("activeTab_".concat(sessionId));
        return stored || defaultTab;
    }
    catch (_a) {
        return defaultTab;
    }
};
var setStoredActiveTab = function (sessionId, tabId) {
    try {
        localStorage.setItem("activeTab_".concat(sessionId), tabId);
    }
    catch (_a) {
        // Ignore localStorage errors
    }
};
export var TabNavigation = function (_a) {
    var _b, _c, _d, _e;
    var tabs = _a.tabs, activeTab = _a.activeTab, onTabChange = _a.onTabChange, className = _a.className, sessionId = _a.sessionId, _f = _a.variant, variant = _f === void 0 ? 'default' : _f, _g = _a.showLabels, showLabels = _g === void 0 ? true : _g;
    var scrollContainerRef = useRef(null);
    var _h = useState(false), showLeftScroll = _h[0], setShowLeftScroll = _h[1];
    var _j = useState(false), showRightScroll = _j[0], setShowRightScroll = _j[1];
    var _k = useState(-1), focusedIndex = _k[0], setFocusedIndex = _k[1];
    var _l = useState(null), hoveredTab = _l[0], setHoveredTab = _l[1];
    var _m = useState({ x: 0, y: 0 }), tooltipPosition = _m[0], setTooltipPosition = _m[1];
    var _o = useState(false), isMobile = _o[0], setIsMobile = _o[1];
    var _p = useState(false), showOverflow = _p[0], setShowOverflow = _p[1];
    // Detect mobile viewport
    useEffect(function () {
        var checkMobile = function () {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return function () { return window.removeEventListener('resize', checkMobile); };
    }, []);
    // Mobile-optimized tabs - use horizontal scrolling instead of overflow menu
    var visibleTabs = React.useMemo(function () {
        if (!isMobile || variant !== 'mobile')
            return tabs;
        // Show all tabs with horizontal scrolling, sorted by priority
        return __spreadArray([], tabs, true).sort(function (a, b) { return (a.priority || 99) - (b.priority || 99); });
    }, [tabs, isMobile, variant]);
    var overflowTabs = React.useMemo(function () {
        // No overflow menu needed with scrolling
        return [];
    }, []);
    // Handle overflow menu body scroll lock
    useEffect(function () {
        if (showOverflow && isMobile) {
            document.body.style.overflow = 'hidden';
        }
        else {
            document.body.style.overflow = '';
        }
        return function () {
            document.body.style.overflow = '';
        };
    }, [showOverflow, isMobile]);
    // Handle tab change with persistence
    var handleTabChange = function (tabId) {
        onTabChange(tabId);
        if (sessionId) {
            setStoredActiveTab(sessionId, tabId);
        }
    };
    // Initialize with stored tab if available
    useEffect(function () {
        var _a, _b;
        if (sessionId && activeTab === ((_a = tabs[0]) === null || _a === void 0 ? void 0 : _a.id)) {
            var storedTab_1 = getStoredActiveTab(sessionId, (_b = tabs[0]) === null || _b === void 0 ? void 0 : _b.id);
            if (storedTab_1 !== activeTab && tabs.find(function (tab) { return tab.id === storedTab_1; })) {
                onTabChange(storedTab_1);
            }
        }
    }, [sessionId, tabs, activeTab, onTabChange]);
    // Check if scrolling is needed
    var checkScrollButtons = function () {
        var container = scrollContainerRef.current;
        if (!container)
            return;
        var scrollLeft = container.scrollLeft, scrollWidth = container.scrollWidth, clientWidth = container.clientWidth;
        setShowLeftScroll(scrollLeft > 0);
        setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    };
    useEffect(function () {
        checkScrollButtons();
        var container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            window.addEventListener('resize', checkScrollButtons);
            return function () {
                container.removeEventListener('scroll', checkScrollButtons);
                window.removeEventListener('resize', checkScrollButtons);
            };
        }
    }, [tabs]);
    // Scroll active tab into view
    useEffect(function () {
        var container = scrollContainerRef.current;
        var activeButton = container === null || container === void 0 ? void 0 : container.querySelector("[data-tab=\"".concat(activeTab, "\"]"));
        if (activeButton && container) {
            var containerRect = container.getBoundingClientRect();
            var buttonRect = activeButton.getBoundingClientRect();
            if (buttonRect.left < containerRect.left || buttonRect.right > containerRect.right) {
                activeButton.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [activeTab]);
    var scroll = function (direction) {
        var container = scrollContainerRef.current;
        if (!container)
            return;
        var scrollAmount = 200;
        var currentScroll = container.scrollLeft;
        var targetScroll = direction === 'left'
            ? currentScroll - scrollAmount
            : currentScroll + scrollAmount;
        container.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
        });
    };
    // Keyboard navigation
    var handleKeyDown = function (event) {
        var currentIndex = tabs.findIndex(function (tab) { return tab.id === activeTab; });
        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                var prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
                if (!tabs[prevIndex].disabled) {
                    handleTabChange(tabs[prevIndex].id);
                }
                break;
            case 'ArrowRight':
                event.preventDefault();
                var nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
                if (!tabs[nextIndex].disabled) {
                    handleTabChange(tabs[nextIndex].id);
                }
                break;
            case 'Home':
                event.preventDefault();
                var firstEnabledTab = tabs.find(function (tab) { return !tab.disabled; });
                if (firstEnabledTab) {
                    handleTabChange(firstEnabledTab.id);
                }
                break;
            case 'End':
                event.preventDefault();
                var lastEnabledTab = tabs.slice().reverse().find(function (tab) { return !tab.disabled; });
                if (lastEnabledTab) {
                    handleTabChange(lastEnabledTab.id);
                }
                break;
        }
    };
    // Handle tooltip positioning
    var handleMouseEnter = function (event, tabId) {
        if (isMobile)
            return; // No tooltips on mobile
        var rect = event.currentTarget.getBoundingClientRect();
        setTooltipPosition({
            x: rect.left + rect.width / 2,
            y: rect.bottom + 8
        });
        setHoveredTab(tabId);
    };
    var handleMouseLeave = function () {
        setHoveredTab(null);
    };
    // Get responsive classes based on variant
    var getTabClasses = function (tab, isActive) {
        var baseClasses = cn("relative flex items-center justify-center transition-all duration-300 whitespace-nowrap group", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", "border border-transparent", tab.disabled && "opacity-50 cursor-not-allowed");
        var variantClasses = {
            default: cn("space-x-1.5 px-3 py-2 rounded-lg font-medium text-sm min-w-fit", isActive
                ? "bg-tab-bar-active/10 text-tab-bar-active shadow-md border-tab-bar-active/20 scale-105"
                : "text-tab-bar-foreground/70 hover:text-tab-bar-foreground hover:bg-tab-bar-active/5", !tab.disabled && !isActive && "hover:scale-102 hover:shadow-sm"),
            compact: cn("space-x-1 px-2 py-1.5 rounded-md font-medium text-xs min-w-fit", isActive
                ? "bg-tab-bar-active text-white shadow-sm"
                : "text-tab-bar-foreground/70 hover:text-tab-bar-foreground hover:bg-tab-bar-active/5"),
            mobile: cn(isMobile ? "flex-col space-y-0.5 px-2 py-2 rounded-lg min-w-[56px] max-w-[68px] flex-shrink-0 min-h-[48px]" : "space-x-1.5 px-3 py-2 rounded-lg", // Better sizing for scrollable tabs
            "font-medium text-xs touch-manipulation", // Added touch-manipulation for better mobile experience
            isActive
                ? "bg-tab-bar-active text-white shadow-sm"
                : "text-tab-bar-foreground/70 hover:text-tab-bar-foreground hover:bg-tab-bar-active/5", !tab.disabled && !isActive && "hover:scale-105")
        };
        return cn(baseClasses, variantClasses[variant]);
    };
    var getIconSize = function () {
        switch (variant) {
            case 'compact': return 'w-3 h-3';
            case 'mobile': return isMobile ? 'w-4 h-4' : 'w-4 h-4';
            default: return 'w-4 h-4';
        }
    };
    return (_jsxs("div", __assign({ className: cn("relative w-full overflow-hidden", className) }, { children: [" ", _jsxs("div", __assign({ className: cn("flex items-center", variant === 'mobile' && isMobile
                    ? "bg-transparent justify-center w-full py-1"
                    : "", variant === 'default' && "justify-center") }, { children: [variant === 'mobile' && isMobile && showLeftScroll && (_jsx("div", { className: "absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background/90 to-transparent z-[5] pointer-events-none mobile-scroll-gradient" })), variant === 'mobile' && isMobile && showRightScroll && (_jsx("div", { className: "absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background/90 to-transparent z-[5] pointer-events-none mobile-scroll-gradient" })), "        ", showLeftScroll && (_jsx(Button, __assign({ variant: "ghost", size: "sm", onClick: function () { return scroll('left'); }, className: cn("absolute left-0 top-1/2 -translate-y-1/2 z-10 shadow-sm hover:bg-background/90", variant === 'mobile' && isMobile
                            ? "bg-background/95 backdrop-blur-sm border border-border/60 w-6 h-6 rounded-full p-1 mobile-scroll-arrow" // Added mobile-scroll-arrow class
                            : "bg-background/80 backdrop-blur-sm border border-border/50"), "aria-label": "Scroll left" }, { children: _jsx(ChevronLeft, { className: variant === 'mobile' && isMobile ? "w-3 h-3" : "w-4 h-4" }) }))), showRightScroll && (_jsx(Button, __assign({ variant: "ghost", size: "sm", onClick: function () { return scroll('right'); }, className: cn("absolute right-0 top-1/2 -translate-y-1/2 z-10 shadow-sm hover:bg-background/90", variant === 'mobile' && isMobile
                            ? "bg-background/95 backdrop-blur-sm border border-border/60 w-6 h-6 rounded-full p-1 mobile-scroll-arrow" // Added mobile-scroll-arrow class
                            : "bg-background/80 backdrop-blur-sm border border-border/50"), "aria-label": "Scroll right" }, { children: _jsx(ChevronRight, { className: variant === 'mobile' && isMobile ? "w-3 h-3" : "w-4 h-4" }) }))), _jsx("div", __assign({ ref: scrollContainerRef, className: cn("flex role-tablist", variant === 'mobile' && isMobile
                            ? "justify-start items-center w-full py-1.5 space-x-1 overflow-x-auto min-h-[48px] scrollbar-hide mobile-tabs-container px-8" // Added padding for scroll arrows
                            : "justify-center space-x-1 overflow-x-auto scrollbar-hide py-1 px-8", variant === 'compact' && "space-x-0.5"), style: { scrollbarWidth: 'none', msOverflowStyle: 'none' }, role: "tablist", onKeyDown: handleKeyDown }, { children: visibleTabs.map(function (tab, index) {
                            var Icon = tab.icon;
                            var isActive = activeTab === tab.id;
                            var isFocused = focusedIndex === index;
                            return (_jsxs("button", __assign({ "data-tab": tab.id, role: "tab", "aria-selected": isActive, "aria-controls": "tabpanel-".concat(tab.id), "aria-label": "".concat(tab.label).concat(tab.description ? " - ".concat(tab.description) : ''), tabIndex: isActive ? 0 : -1, disabled: tab.disabled, onClick: function () { return !tab.disabled && handleTabChange(tab.id); }, onFocus: function () { return setFocusedIndex(index); }, onBlur: function () { return setFocusedIndex(-1); }, onMouseEnter: function (e) { return handleMouseEnter(e, tab.id); }, onMouseLeave: handleMouseLeave, className: cn(getTabClasses(tab, isActive), variant === 'mobile' && isMobile && "mobile-tab"), style: isActive && tab.color
                                    ? {
                                        background: "var(--tab-gradient-primary)",
                                        color: 'white',
                                        borderColor: 'transparent'
                                    }
                                    : {} }, { children: [_jsx(Icon, { className: cn(getIconSize(), "transition-transform duration-200", isActive && "scale-110") }), showLabels && (variant !== 'mobile' || !isMobile) && (_jsx("span", __assign({ className: "font-medium" }, { children: tab.label }))), variant === 'mobile' && isMobile && (_jsx("span", __assign({ className: "text-xs font-medium leading-none truncate w-full text-center overflow-hidden max-w-[50px]" }, { children: tab.label }))), tab.badge && !(variant === 'mobile' && isMobile) && (_jsx(Badge, __assign({ className: cn("ml-2 text-xs transition-colors duration-200", isActive
                                            ? "bg-background/20 text-current border-current/20"
                                            : "bg-primary/10 text-primary border-primary/20") }, { children: tab.badge }))), isActive && (_jsx("div", { className: cn("absolute transform", variant === 'mobile' && isMobile
                                            ? "top-0.5 left-1/2 -translate-x-1/2 w-1 h-1"
                                            : "-bottom-1 left-1/2 -translate-x-1/2 w-2 h-2", "bg-current rounded-full shadow-sm") })), !isActive && (_jsx("div", { className: "absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" }))] }), tab.id));
                        }) }))] })), hoveredTab && !isMobile && (_jsx("div", __assign({ className: "fixed z-50 pointer-events-none", style: {
                    left: tooltipPosition.x,
                    top: tooltipPosition.y,
                    transform: 'translateX(-50%)'
                } }, { children: _jsxs("div", __assign({ className: "bg-popover text-popover-foreground px-3 py-2 rounded-lg shadow-lg text-sm max-w-xs border border-border" }, { children: [_jsx("div", __assign({ className: "font-medium" }, { children: (_b = tabs.find(function (tab) { return tab.id === hoveredTab; })) === null || _b === void 0 ? void 0 : _b.label })), _jsx("div", __assign({ className: "text-xs text-muted-foreground mt-1" }, { children: (_c = tabs.find(function (tab) { return tab.id === hoveredTab; })) === null || _c === void 0 ? void 0 : _c.description })), ((_d = tabs.find(function (tab) { return tab.id === hoveredTab; })) === null || _d === void 0 ? void 0 : _d.shortcut) && (_jsx("div", __assign({ className: "text-xs text-muted-foreground mt-1 font-mono" }, { children: (_e = tabs.find(function (tab) { return tab.id === hoveredTab; })) === null || _e === void 0 ? void 0 : _e.shortcut }))), _jsx("div", { className: "absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-popover rotate-45 border-l border-t border-border" })] })) }))), _jsx("style", { children: "\n        .scrollbar-hide {\n          -ms-overflow-style: none;\n          scrollbar-width: none;\n        }\n        .scrollbar-hide::-webkit-scrollbar {\n          display: none;\n        }\n      " })] })));
};
